import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import * as Api from '@ViewModels';
import React from 'react';
import { EditModeCombobox, EditModeNumberInput, EditModeSelect, EditModeToggle } from '../context';
import type {
	ComboboxField,
	ComplementSelectField,
	LockableSelectField,
	NumberField,
	SelectField,
	SettingsField,
	ToggleField,
} from '../providers/types';
import {
	createFieldUpdate,
	excludeValues,
	getFieldOptions,
	getFieldsDisplayValue,
	getFieldValue,
	getSelectDisplayValue,
	isFieldVisible,
} from '../providers/utils';
import { styleSheet } from '../styles';
import { SettingRow } from './SettingRow';

const NONE_VALUE = '__none__' as const;

interface SettingsFieldRendererProps {
	field: SettingsField;
	settings: Api.IIntegrationSettings | Api.ITagSettings | Api.IKeyFactSettings;
	onUpdate: (updates: Record<string, unknown>) => void;
	configuration: Api.IIntegrationConfiguration;
	/** For lockable select fields: provides the original persisted value for a given key */
	getOriginalValue?: <T>(key: string) => T | undefined;
	/** Add a "None" option for single-select combobox fields (maps to empty string on change) */
	singleSelectNoneOption?: boolean;
}

function renderToggle(
	field: ToggleField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate']
) {
	const value = getFieldValue<boolean>(settings, field) ?? false;
	return (
		<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
			<EditModeToggle
				id={field.key}
				isOn={value}
				onToggleCheckChanged={(checked: boolean) => onUpdate(createFieldUpdate(settings, field, checked))}
			/>
		</SettingRow>
	);
}

function renderSelect(
	field: SelectField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate'],
	configuration: Api.IIntegrationConfiguration
) {
	const value = getFieldValue<string>(settings, field);
	const options = field.staticOptions ?? getFieldOptions(field.optionsFrom ?? '', { configuration });
	return (
		<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
			<EditModeSelect
				items={options.map(o => ({ label: o.label, value: o.id }))}
				value={value ?? null}
				onValueChange={(newValue: string) => onUpdate(createFieldUpdate(settings, field, newValue))}
				displayValue={getSelectDisplayValue(value, options)}
				disabled={field.disabled}
			>
				<SelectTrigger styles={styleSheet.editableField}>
					<SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
				</SelectTrigger>
				<SelectContent>
					{options.map(o => (
						<SelectItem key={o.id} value={o.id}>
							{o.label}
						</SelectItem>
					))}
				</SelectContent>
			</EditModeSelect>
		</SettingRow>
	);
}

function renderLockableSelect(
	field: LockableSelectField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate'],
	configuration: Api.IIntegrationConfiguration,
	getOriginalValue?: SettingsFieldRendererProps['getOriginalValue']
) {
	const value = getFieldValue<string>(settings, field);
	const originalValue = getOriginalValue?.<string>(field.key);
	const options = getFieldOptions(field.optionsFrom, { configuration });

	const isLocked = originalValue !== field.initialValue;
	const showError = value === field.initialValue;

	return (
		<SettingRow
			key={field.key}
			title={field.label}
			subtitle={field.subtitle}
			error={showError ? field.initialValueError : null}
			isSubSetting={field.isSubSetting}
		>
			<EditModeSelect
				value={value}
				onValueChange={(newValue: string) => onUpdate(createFieldUpdate(settings, field, newValue))}
				displayValue={value || field.placeholder}
				disabled={isLocked}
			>
				<SelectTrigger styles={styleSheet.editableField}>
					<SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
				</SelectTrigger>
				<SelectContent>
					{field.showInitialValueOption && !isLocked && (
						<SelectItem key={field.initialValue} value={field.initialValue}>
							{field.initialValue}
						</SelectItem>
					)}
					{options.map(o => (
						<SelectItem key={o.id} value={o.id}>
							{o.label}
						</SelectItem>
					))}
				</SelectContent>
			</EditModeSelect>
		</SettingRow>
	);
}

function renderCombobox(
	field: ComboboxField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate'],
	configuration: Api.IIntegrationConfiguration,
	singleSelectNoneOption?: boolean
) {
	let options = getFieldOptions(field.optionsFrom, { configuration });
	if (field.hideWhenNoOptions && !options.length) return null;
	if (!field.multiple && singleSelectNoneOption) {
		options = [{ id: NONE_VALUE, label: 'None' }, ...options];
	}
	const optionsRecord = Object.fromEntries(options.map(o => [o.id, o.label]));

	if (field.multiple) {
		const selectedValues = getFieldValue<string[]>(settings, field) ?? [];
		return (
			<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
				<EditModeCombobox
					multiple
					items={options}
					value={selectedValues}
					onChange={(newValue: string[]) => onUpdate(createFieldUpdate(settings, field, newValue))}
					displayValue={getFieldsDisplayValue(selectedValues, optionsRecord)}
					searchPlaceholder={field.searchPlaceholder || `Search ${field.label.toLowerCase()}...`}
				/>
			</SettingRow>
		);
	}

	const rawValue = singleSelectNoneOption ? getFieldValue<string>(settings, field) : undefined;
	const value = singleSelectNoneOption ? rawValue || NONE_VALUE : (getFieldValue<string>(settings, field) ?? '');

	return (
		<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
			<EditModeCombobox
				multiple={false}
				items={options}
				value={value}
				onChange={(newValue: string) => {
					if (singleSelectNoneOption) {
						onUpdate(createFieldUpdate(settings, field, newValue === NONE_VALUE ? '' : newValue));
					} else {
						onUpdate(createFieldUpdate(settings, field, newValue));
					}
				}}
				displayValue={
					singleSelectNoneOption ? getSelectDisplayValue(rawValue, options) : optionsRecord[value] || 'None selected'
				}
				searchPlaceholder={field.searchPlaceholder || `Search ${field.label.toLowerCase()}...`}
			/>
		</SettingRow>
	);
}

function renderComplementSelect(
	field: ComplementSelectField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate'],
	configuration: Api.IIntegrationConfiguration
) {
	const options = getFieldOptions(field.optionsFrom, { configuration });
	// Hide the selector entirely when the source list is empty (matches the config-sourced precedent).
	if (!options.length) return null;

	const allOptionIds = options.map(o => o.id);
	const optionsRecord = Object.fromEntries(options.map(o => [o.id, o.label]));
	const storedComplement = getFieldValue<string[]>(settings, field) ?? [];
	// The user selects the kept options; selected = all options - the stored complement.
	const selectedIds = excludeValues(allOptionIds, storedComplement);

	return (
		<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
			<EditModeCombobox
				multiple
				items={options}
				value={selectedIds}
				// Persist the complement of the selection: complement = all options - selected.
				// Computed against the current option set, so values no longer present are dropped.
				onChange={(selected: string[]) =>
					onUpdate(createFieldUpdate(settings, field, excludeValues(allOptionIds, selected)))
				}
				displayValue={getFieldsDisplayValue(selectedIds, optionsRecord)}
				searchPlaceholder={field.searchPlaceholder || `Search ${field.label.toLowerCase()}...`}
			/>
		</SettingRow>
	);
}

function renderNumber(
	field: NumberField,
	settings: SettingsFieldRendererProps['settings'],
	onUpdate: SettingsFieldRendererProps['onUpdate']
) {
	const value = getFieldValue<number>(settings, field) ?? field.min ?? 0;

	const getDisplayValue = () => {
		if (field.displaySuffix) {
			const plural = value === 1 ? '' : 's';
			return `${value} ${field.displaySuffix}${plural}`;
		}
		return String(value);
	};

	return (
		<SettingRow key={field.key} title={field.label} subtitle={field.subtitle} isSubSetting={field.isSubSetting}>
			<EditModeNumberInput
				value={value}
				onChange={(newValue: number) => onUpdate(createFieldUpdate(settings, field, newValue))}
				displayValue={getDisplayValue()}
				min={field.min}
				max={field.max}
				placeholder={field.placeholder}
				customStyles={[styleSheet.numberInputSmall]}
			/>
		</SettingRow>
	);
}

export const SettingsFieldRenderer: React.FC<SettingsFieldRendererProps> = ({
	field,
	settings,
	onUpdate,
	configuration,
	getOriginalValue,
	singleSelectNoneOption,
}) => {
	// Check visibility condition
	if (!isFieldVisible(settings, field.visibleWhen)) {
		return null;
	}

	switch (field.type) {
		case 'toggle':
			return renderToggle(field, settings, onUpdate);
		case 'select':
			return renderSelect(field, settings, onUpdate, configuration);
		case 'lockableSelect':
			return renderLockableSelect(field, settings, onUpdate, configuration, getOriginalValue);
		case 'combobox':
			return renderCombobox(field, settings, onUpdate, configuration, singleSelectNoneOption);
		case 'number':
			return renderNumber(field, settings, onUpdate);
		case 'complementSelect':
			return renderComplementSelect(field, settings, onUpdate, configuration);
		default:
			return null;
	}
};
